import React, { useEffect, useState } from 'react';
import { FormControl, Button, Grid, FormHelperText, Select, MenuItem, Avatar, OutlinedInput, FormLabel } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { putRequest, resetPut } from '../../../redux/modules/profile/slice';
import { toast } from 'react-toastify';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from 'moment/moment';
import SignatureCanvas from './signatureCanvas';

const getValidationSchema = (userData)=>{
    return yup.object().shape({
    avatar: yup.mixed()
        .test('file-type', 'Invalid file type', function (value) {
            if (!value) return true; // If no file is provided, consider it valid
            const allowedFileTypes = ['image/jpeg', 'image/png']; // Adjust the allowed file types as needed
            return allowedFileTypes.includes(value.type);
        }),
    firstName: yup.string().required('First Name is required'),
    lastName: yup.string(),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup.lazy(value => {
        switch (typeof value) {
          case 'string':
            return value.trim() === '' ? yup.string().nullable() : yup.string()
              .matches(/^[0-9]*$/, 'Phone number must contain only digits')
              .min(7, 'Phone number must be at least 7 digits')
              .max(15, 'Phone number cannot exceed 15 digits')
          default:
            return yup.string().nullable();
        }
      }),
    gender: yup.string(),
    dob: yup.string().nullable(),
    country: yup.string(),
    state: yup.string(),
    city: yup.string(),
    zipCode: yup.string(),
    taxId: yup.string(),
    signatureImage: yup.string(),
    ...(userData && userData?.role !== "Patient" && {
        signaturePin: yup.string().when('signatureImage', {
            is: (val) => val && val.length > 0,
            then: (schema) => schema.required('Signature PIN is required when a signature is set'),
            otherwise: (schema) => schema,
        }),
        credentials: yup.string(),
    }),
});
};

const ProfileForm = ({ itemData, toggleList }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(state => state.auth);
    const validationSchema = getValidationSchema(userData);
    const [avatarFile, setAvatarFile] = useState(Boolean(itemData?.avatar));
    const [isSignatureFile, setSignatureFile] = useState(Boolean(itemData?.signatureImage));
    let { loading, putRes } = useSelector(state => state.profile);
    const { register, handleSubmit, formState: { errors }, getValues, setValue, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { ...itemData, avatar: "", signatureImage: "" }
    });

    useEffect(() => {
        if (!putRes) { return }
        if (putRes?.success) {
            toggleList();
        }
        toast(putRes?.message);
        dispatch(resetPut());
    }, [putRes, toggleList, dispatch]);

    const onSubmit = (data) => {
        if (!itemData?._id) { toast.error("Something went wrong. Please try again later."); return; }
        const formData = new FormData();
        for (const i in data) {
            if (i === "avatar") {
                if (!avatarFile && data[i]) {
                    // User removed old avatar and uploaded a new file
                    formData.append("avatar", data[i]);
                } else if (!avatarFile && !data[i]) {
                    // User removed old avatar without uploading a new one
                    formData.append("avatar", "__REMOVE__");
                }
                // If avatarFile is true, user didn't touch the avatar — don't send anything
                continue;
            }
            else if (i === "signatureImage") {
                if (!isSignatureFile && data[i]) {
                    // User removed old signature and drew/uploaded a new one
                    formData.append("signatureImage", data[i]);
                } else if (!isSignatureFile && !data[i]) {
                    // User removed old signature without adding a new one
                    formData.append("signatureImage", "__REMOVE__");
                }
                // If isSignatureFile is true, user didn't touch the signature — don't send anything
                continue;
            }
            formData.append(i, data[i]);
        }
        dispatch(putRequest(formData));
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormLabel>Avatar</FormLabel>
                    {avatarFile
                        ?
                        <>
                            <Avatar
                                alt={itemData?.firstName}
                                src={`${process.env.REACT_APP_SERVER_API}api/profile/${itemData?._id}/avatar/${itemData?.avatar}`}
                                sx={{ width: 100, height: 100 }} />
                            <Button onClick={() => { setAvatarFile(false) }}>Remove Image</Button>
                        </>
                        :
                        <FormControl fullWidth error={Boolean(errors.avatar)}>
                            <OutlinedInput className="inputfield" type="file" fullWidth onChange={(e) => {
                                setValue('avatar', e.target.files[0]);
                                trigger("avatar")
                            }} />
                            {errors?.avatar?.message && <FormHelperText>{errors?.avatar?.message}</FormHelperText>}
                        </FormControl>
                    }
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.firstName)}>
                        <FormLabel>First name</FormLabel>
                        <OutlinedInput className="inputfield" fullWidth {...register('firstName')} />
                        {errors?.firstName?.message && <FormHelperText>{errors?.firstName?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.lastName)}>
                        <FormLabel>Last name</FormLabel>
                        <OutlinedInput className="inputfield" fullWidth {...register('lastName')} />
                        {errors?.lastName?.message && <FormHelperText>{errors?.lastName?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.email)}>
                        <FormLabel>Email</FormLabel>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('email')} />
                        {errors?.email?.message && <FormHelperText>{errors?.email?.message}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.phone)}>
                        <FormLabel>Phone Number</FormLabel>
                        <OutlinedInput className="inputfield" label=""  fullWidth {...register('phone')} />
                        {errors?.phone?.message && <FormHelperText>{errors?.phone?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.dob)}>
                        <FormLabel>Date of birth</FormLabel>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker className="inputfield" disableFuture={true} format="DD/MM/YYYY" value={moment.utc(getValues().dob)} onChange={(date) => { setValue("dob", moment(date).format('YYYY-MM-DD')); trigger("dob") }} />
                        </LocalizationProvider>
                        {errors?.dob?.message && <FormHelperText>{errors?.dob?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth>
                        <FormLabel>Gender</FormLabel>
                        <Select className='inputfield' label="Gender" defaultValue={itemData?.gender || "male"} {...register('gender')}>
                            <MenuItem value={"male"}>Male</MenuItem>
                            <MenuItem value={"female"}>Female</MenuItem>
                            <MenuItem value={"other"}>Other</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormLabel>Country</FormLabel>
                    <FormControl fullWidth error={Boolean(errors.country)}>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('country')} />
                        {errors?.country?.message && <FormHelperText>{errors?.country?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.state)}>
                        <FormLabel>State</FormLabel>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('state')} />
                        {errors?.state?.message && <FormHelperText>{errors?.state?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.city)}>
                        <FormLabel>City</FormLabel>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('city')} />
                        {errors?.city?.message && <FormHelperText>{errors?.city?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.zipCode)}>
                        <FormLabel>Zip Code</FormLabel>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('zipCode')} />
                        {errors?.zipCode?.message && <FormHelperText>{errors?.zipCode?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                    <FormControl fullWidth error={Boolean(errors.taxId)}>
                        <FormLabel>Tax ID</FormLabel>
                        <OutlinedInput className="inputfield" label="" fullWidth {...register('taxId')} />
                        {errors?.taxId?.message && <FormHelperText>{errors?.taxId?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                {userData?.role !== "Patient" ? (<>
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                        <FormControl fullWidth error={Boolean(errors.signaturePin)}>
                            <FormLabel>Signature PIN</FormLabel>
                            <OutlinedInput className="inputfield" label="" fullWidth {...register('signaturePin')} />
                            <FormHelperText error={Boolean(errors.signaturePin)}>
                                {errors?.signaturePin?.message || 'Required to sign reports. Enter a PIN you\'ll use to authorize your signature.'}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} xs={12}>
                        <FormLabel>Signature Image</FormLabel>
                        {isSignatureFile
                            ?
                            <div>
                                <img alt={itemData?.firstName} src={`${process.env.REACT_APP_SERVER_API}api/profile/${itemData?._id}/signatureImage/signature.png`} style={{ height: "100px" }} />
                                <div>
                                    <Button onClick={() => { setSignatureFile(false) }}>Remove Image</Button>
                                </div>
                            </div>
                            :
                            <SignatureCanvas getValues={getValues} setValue={setValue} trigger={trigger} />
                        }
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <FormControl fullWidth error={Boolean(errors.credentials)}>
                            <FormLabel>Credentials</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                fullWidth
                                {...register('credentials')}
                                placeholder="e.g., MD, RDMS, RVT - appears below signature on reports"
                            />
                            <FormHelperText>
                                {errors?.credentials?.message || 'This text appears below your signature on reports (e.g., "MD, RDMS, FAIUM")'}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                </>):null}
                
                <Grid item xs={12} textAlign={"right"}>
                    <Button type="button" sx={{ marginRight: 2 }} variant='contained' size="large" disabled={loading} onClick={toggleList}>
                        Cancel
                    </Button>
                    <Button type="submit" variant='contained' size="large" disabled={loading}>
                        {itemData?._id ? "Update" : "Submit"}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default ProfileForm;